import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Clock, Calendar as CalIcon, Download } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const eventTypeColors = {
  event: 'bg-brand-gold/20 text-brand-gold border-brand-gold/40',
  workshop: 'bg-blue-100 text-blue-700 border-blue-300',
  meeting: 'bg-gray-100 text-gray-700 border-gray-300',
  fundraiser: 'bg-green-100 text-green-700 border-green-300',
  retreat: 'bg-purple-100 text-purple-700 border-purple-300',
};

const CalendarPage = () => {
  usePageTitle('Events Calendar');
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const m = String(currentMonth + 1).padStart(2, '0');
        const res = await fetch(`${API}/calendar-events?year=${currentYear}&month=${m}`);
        if (res.ok) setEvents(await res.json());
      } catch (err) {
        console.error('Error fetching events:', err);
      }
      setLoading(false);
    };
    fetchEvents();
  }, [currentMonth, currentYear]);

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const goToToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  // Build calendar grid
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return events.filter(e => {
      if (e.start_date === dateStr) return true;
      if (e.end_date && e.start_date <= dateStr && e.end_date >= dateStr) return true;
      return false;
    });
  };

  const isToday = (day) => {
    return day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
  };

  const handleAddToCalendar = (eventId) => {
    window.open(`${API}/calendar-events/${eventId}/ics`, '_blank');
  };

  // Upcoming events list (all events in and after current month)
  const upcomingEvents = events
    .filter(e => {
      const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
      return (e.end_date || e.start_date) >= todayStr;
    })
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  return (
    <div className="bg-white min-h-screen" data-testid="calendar-page">
      {/* Hero */}
      <div className="bg-brand-dark py-16">
        <div className="mx-auto max-w-6xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-semibold text-white mb-4" data-testid="calendar-heading">
            Events Calendar
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            Stay up to date with ElevateMN programs, workshops, and community events.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6 sm:mb-8" data-testid="calendar-nav">
          <div className="flex items-center gap-3">
            <button onClick={prevMonth} className="p-2 hover:bg-gray-100 transition-colors" data-testid="prev-month-btn">
              <ChevronLeft className="w-5 h-5 text-brand-dark" />
            </button>
            <h2 className="text-lg sm:text-xl font-semibold text-brand-dark min-w-[160px] sm:min-w-[200px] text-center" data-testid="current-month">
              {MONTHS[currentMonth]} {currentYear}
            </h2>
            <button onClick={nextMonth} className="p-2 hover:bg-gray-100 transition-colors" data-testid="next-month-btn">
              <ChevronRight className="w-5 h-5 text-brand-dark" />
            </button>
          </div>
          <button
            onClick={goToToday}
            className="text-sm text-brand-gold font-medium hover:underline"
            data-testid="today-btn"
          >
            Today
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
          {/* Calendar Grid */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200">
              {/* Day headers */}
              {DAYS.map(day => (
                <div key={day} className="bg-brand-dark px-1 sm:px-2 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-white">
                  {day}
                </div>
              ))}
              {/* Day cells */}
              {cells.map((day, idx) => {
                const dayEvents = getEventsForDay(day);
                return (
                  <div
                    key={idx}
                    className={`bg-white min-h-[60px] sm:min-h-[80px] p-1 sm:p-1.5 ${!day ? 'bg-gray-50' : ''} ${isToday(day) ? 'ring-2 ring-inset ring-brand-gold' : ''}`}
                  >
                    {day && (
                      <>
                        <span className={`text-xs font-medium ${isToday(day) ? 'text-brand-gold' : 'text-gray-700'}`}>
                          {day}
                        </span>
                        <div className="mt-1 space-y-0.5">
                          {dayEvents.slice(0, 2).map(ev => (
                            <button
                              key={ev.id}
                              onClick={() => setSelectedEvent(ev)}
                              className={`block w-full text-left text-[10px] leading-tight px-1 py-0.5 truncate border rounded ${eventTypeColors[ev.event_type] || eventTypeColors.event}`}
                              data-testid={`cal-event-${ev.id}`}
                            >
                              {ev.title}
                            </button>
                          ))}
                          {dayEvents.length > 2 && (
                            <span className="text-[10px] text-gray-500 px-1">+{dayEvents.length - 2} more</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar: Upcoming Events or Event Detail */}
          <div>
            {selectedEvent ? (
              <div className="bg-gray-50 p-6" data-testid="event-detail">
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-sm text-gray-500 hover:text-brand-dark mb-4"
                >
                  ← Back to list
                </button>
                <span className={`inline-block text-xs px-2 py-0.5 border rounded mb-3 ${eventTypeColors[selectedEvent.event_type] || eventTypeColors.event}`}>
                  {selectedEvent.event_type}
                </span>
                <h3 className="text-lg font-semibold text-brand-dark mb-3" data-testid="event-detail-title">
                  {selectedEvent.title}
                </h3>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <p className="flex items-center gap-2">
                    <CalIcon className="w-4 h-4 text-brand-gold" />
                    {formatDisplayDate(selectedEvent.start_date)}
                    {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.start_date && (
                      <> – {formatDisplayDate(selectedEvent.end_date)}</>
                    )}
                  </p>
                  {selectedEvent.start_time && (
                    <p className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-brand-gold" />
                      {selectedEvent.start_time}{selectedEvent.end_time && ` – ${selectedEvent.end_time}`}
                    </p>
                  )}
                  {selectedEvent.location && (
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-brand-gold" />
                      {selectedEvent.location}
                    </p>
                  )}
                </div>
                {selectedEvent.description && (
                  <p className="text-sm text-gray-700 leading-relaxed mb-4 whitespace-pre-wrap">
                    {selectedEvent.description}
                  </p>
                )}
                <button
                  onClick={() => handleAddToCalendar(selectedEvent.id)}
                  className="flex items-center gap-2 bg-brand-gold px-4 py-2 text-sm font-medium text-brand-dark hover:opacity-90"
                  data-testid="add-to-calendar-btn"
                >
                  <Download className="w-4 h-4" />
                  Add to Calendar
                </button>
              </div>
            ) : (
              <div data-testid="upcoming-events-list">
                <h3 className="text-lg font-semibold text-brand-dark mb-4">
                  Upcoming Events
                </h3>
                {loading ? (
                  <p className="text-gray-500 text-sm">Loading...</p>
                ) : upcomingEvents.length === 0 ? (
                  <p className="text-gray-500 text-sm">No upcoming events this month.</p>
                ) : (
                  <div className="space-y-4">
                    {upcomingEvents.map(ev => (
                      <button
                        key={ev.id}
                        onClick={() => setSelectedEvent(ev)}
                        className="block w-full text-left bg-gray-50 p-4 hover:bg-gray-100 transition-colors"
                        data-testid={`upcoming-event-${ev.id}`}
                      >
                        <span className={`inline-block text-[10px] px-1.5 py-0.5 border rounded mb-2 ${eventTypeColors[ev.event_type] || eventTypeColors.event}`}>
                          {ev.event_type}
                        </span>
                        <h4 className="font-medium text-brand-dark text-sm">{ev.title}</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDisplayDate(ev.start_date)}
                          {ev.location && ` · ${ev.location}`}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
